import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Package, 
  CreditCard, 
  User, 
  MapPin, 
  Truck, 
  Clock,
  CheckCircle,
  XCircle,
  Mail,
  Phone,
  ShoppingBag,
  RefreshCw,
  Wallet
} from 'lucide-react';
import { ordersService, OrderDetailResponse } from '@/api/services/orders';
import { formatCurrency } from '@/utils/currency';
import type { ApiOrder, OrderItem, Payment } from '../types';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  NEW: { label: 'New', variant: 'secondary' },
  PENDING: { label: 'Pending', variant: 'secondary' },
  PROCESSING: { label: 'Processing', variant: 'outline' },
  SHIPPED: { label: 'Shipped', variant: 'outline' },
  DELIVERED: { label: 'Delivered', variant: 'default' },
  CANCELLED: { label: 'Cancelled', variant: 'destructive' },
};

const paymentStatusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  PENDING: { label: 'Pending', variant: 'outline' },
  SUCCESS: { label: 'Success', variant: 'default' },
  FAILED: { label: 'Failed', variant: 'destructive' },
};

function formatAddress(address: ApiOrder['shipping_address']): string {
  if (!address) return 'N/A';
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postal_code,
    address.country,
  ].filter(Boolean);
  return parts.join(', ');
}

export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState<ApiOrder | null>(null);
  const [items, setItems] = useState<OrderItem[]>([]);
  const [payment, setPayment] = useState<Payment | undefined>(undefined);

  const formatOrderCurrency = (amount: number) => formatCurrency(amount, 2);

  useEffect(() => {
    if (id) {
      fetchOrderDetail(id);
    }
  }, [id]);

  const fetchOrderDetail = async (orderId: string) => {
    setLoading(true);
    try {
      const data: OrderDetailResponse = await ordersService.getOrder(orderId);
      setOrder(data.order);
      setItems(data.items);
      setPayment(data.payment ?? undefined);
    } catch (error) {
      console.error('Failed to fetch order detail:', error);
      toast.error('Failed to fetch order details');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!order) return;

    if (newStatus === 'CANCELLED') {
      const reason = window.prompt('Enter cancellation reason:');
      if (reason === null) return;

      try {
        await ordersService.cancelOrder(order.id, reason);
        await fetchOrderDetail(order.id);
        toast.success('Order cancelled successfully');
      } catch (error) {
        console.error('Failed to cancel order:', error);
        toast.error('Failed to cancel order');
      }
      return;
    }

    try {
      await ordersService.updateOrder(order.id, { status: newStatus as any });
      await fetchOrderDetail(order.id);
      toast.success('Order status updated');
    } catch (error) {
      console.error('Failed to update order:', error);
      toast.error('Failed to update order status');
    }
  };

  const handlePayment = async () => {
    if (!order) return;
    
    try {
      const response = await fetch(`${import.meta.env.VITE_ORDER_MGMT_API_URL}/api/payments/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: order.id,
          cash_on_delivery: order.cash_on_delivery || false,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to create payment');
      }

      const data = await response.json();
      
      if (data.payment_url) {
        window.location.href = data.payment_url;
      } else {
        toast.success('Payment initiated successfully');
        await fetchOrderDetail(order.id);
      }
    } catch (error) {
      console.error('Failed to initiate payment:', error);
      toast.error('Failed to initiate payment');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-muted rounded w-1/4"></div>
                <div className="h-8 bg-muted rounded w-1/2"></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/orders')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Orders
        </Button>
        <Card>
          <CardContent className="p-6 text-center">
            <p className="text-muted-foreground">Order not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const showPaymentButton = !order.cash_on_delivery && (!payment || payment.status !== 'SUCCESS');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Orders
          </Button>
          <div>
            <h2 className="text-2xl font-semibold">Order {order.external_order_id || order.id.slice(0, 8).toUpperCase()}</h2>
            <p className="text-muted-foreground">
              Placed on {new Date(order.created_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => fetchOrderDetail(order.id)}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Select value={order.status} onValueChange={handleStatusChange}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(statusConfig).map(([status, config]) => (
                <SelectItem key={status} value={status}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="w-5 h-5" />
                Order Items
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>SKU</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-center">Quantity</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell className="text-muted-foreground">{item.sku || '-'}</TableCell>
                      <TableCell className="text-right">{formatOrderCurrency(item.unit_price)}</TableCell>
                      <TableCell className="text-center">{item.quantity}</TableCell>
                      <TableCell className="text-right font-medium">{formatOrderCurrency(item.total_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="mt-6 space-y-2 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>{formatOrderCurrency(order.subtotal_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Tax</span>
                  <span>{formatOrderCurrency(order.tax_amount)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Shipping</span>
                  <span>{formatOrderCurrency(order.shipping_amount)}</span>
                </div>
                {order.discount_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Discount</span>
                    <span className="text-green-600">-{formatOrderCurrency(order.discount_amount)}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>Total</span>
                  <span>{formatOrderCurrency(order.total_amount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Shipping Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Shipping Address</h4>
                    {order.shipping_address ? (
                      <div className="space-y-1">
                        <p className="text-sm">{order.customer_name || 'N/A'}</p>
                        <p className="text-sm">{order.shipping_address.line1}</p>
                        {order.shipping_address.line2 && <p className="text-sm">{order.shipping_address.line2}</p>}
                        <p className="text-sm">
                          {order.shipping_address.city}, {order.shipping_address.state} {order.shipping_address.postal_code}
                        </p>
                        <p className="text-sm">{order.shipping_address.country}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No shipping address</p>
                    )}
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-1">Billing Address</h4>
                    {order.billing_address ? (
                      <div className="space-y-1">
                        <p className="text-sm">{order.customer_name || 'N/A'}</p>
                        <p className="text-sm">{order.billing_address.line1}</p>
                        {order.billing_address.line2 && <p className="text-sm">{order.billing_address.line2}</p>}
                        <p className="text-sm">
                          {order.billing_address.city}, {order.billing_address.state} {order.billing_address.postal_code}
                        </p>
                        <p className="text-sm">{order.billing_address.country}</p>
                      </div>
                    ) : order.shipping_address ? (
                      <div className="space-y-1">
                        <p className="text-sm">Same as shipping address</p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">No billing address</p>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Customer Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1">Customer Name</h4>
                <p className="font-medium">{order.customer_name || 'N/A'}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </h4>
                <p className="text-sm">{order.customer_email || 'N/A'}</p>
              </div>
              {order.customer_phone && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-1 flex items-center gap-1">
                    <Phone className="w-3 h-3" /> Phone
                  </h4>
                  <p className="text-sm">{order.customer_phone}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {!order.cash_on_delivery ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Payment Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {payment ? (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Payment Provider</h4>
                      <p className="text-sm capitalize">{payment.provider.replace('_', ' ')}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Amount</h4>
                      <p className="text-2xl font-bold">{formatOrderCurrency(payment.amount)}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Status</h4>
                      <Badge variant={paymentStatusConfig[payment.status]?.variant || 'outline'}>
                        {paymentStatusConfig[payment.status]?.label || payment.status}
                      </Badge>
                    </div>
                    {payment.payment_method && (
                      <div>
                        <h4 className="text-sm font-medium text-muted-foreground mb-1">Payment Method</h4>
                        <p className="text-sm capitalize">{payment.payment_method}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Amount</h4>
                      <p className="text-2xl font-bold">{formatOrderCurrency(order.total_amount)}</p>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-muted-foreground mb-1">Status</h4>
                      <Badge variant="outline">Pending Payment</Badge>
                    </div>
                  </>
                )}
                {showPaymentButton && (
                  <Button className="w-full mt-4" onClick={handlePayment}>
                    <Wallet className="w-4 h-4 mr-2" />
                    Pay Now
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5" />
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Cash on Delivery</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Payment will be collected at the time of delivery
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Order Timeline
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">Order Placed</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(order.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
                {order.status !== 'NEW' && order.status !== 'CANCELLED' && (
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Package className="w-4 h-4 text-blue-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Processing</p>
                      <p className="text-xs text-muted-foreground">Order is being processed</p>
                    </div>
                  </div>
                )}
                {order.status === 'SHIPPED' && (
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <Truck className="w-4 h-4 text-orange-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Shipped</p>
                      <p className="text-xs text-muted-foreground">Order has been shipped</p>
                    </div>
                  </div>
                )}
                {order.status === 'DELIVERED' && (
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Delivered</p>
                      <p className="text-xs text-muted-foreground">Order delivered successfully</p>
                    </div>
                  </div>
                )}
                {order.status === 'CANCELLED' && (
                  <div className="flex items-start gap-3">
                    <div className="mt-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">Cancelled</p>
                      <p className="text-xs text-muted-foreground">Order was cancelled</p>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
